import { formatDate, defaultField, defaultPercentField, mapSubTextFrequency } from "helpers/row-logic-helper";
import { SIDE_TABLE_COLUMN_TYPES, tableSideColumnMapper } from "@loanbossinc/component-library";

import { shouldShowField } from "../loanDetailEditUtil";

import {
  shouldShowDscrDebtServiceTermTrailingMonths,
  shouldShowDscrDebtServiceTermProjectedMonths,
  shouldHypotheticalAmortizations,
  shouldShowDscrStabilized,
  shouldShowDscrCovenants,
  shouldShowDebtYieldStabilized,
  shouldShowDebtYieldCovenants,
  shouldShowLtvStabilized,
  shouldShowLtvCovenants,
  shouldShowOccupancy,
  shouldShowdscrType,
  shouldShowOccupancyCovenants,
  shouldShowLtc,
  shouldShowLtcCovenants,
  shouldShowDscrAsIs,
  shouldShowDebtYieldAsIs,
  shouldShowLtvAsIs,
  shouldShowFinancialCovenantsMisc,
  shouldShowDscrSubSection,
  shouldShowDebtYieldSubSection,
  shouldShowLtvSubSection,
  shouldShowOccupancySubSection,
  shouldShowLtcSubSection,
  shouldShowAsIsSubSection
} from "./section-financial-covenants-rows-logic";

const DEFAULT_WIDTH = 138;

export const rows = {
  dscrSubSection: {
    isVisible: loan => shouldShowDscrSubSection(loan),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        className: "subsection",
        width: DEFAULT_WIDTH,
        label: "dscr",
        Cell: tableSideColumnMapper,
        colSpan: 3
      }
    ]
  },
  dscrStabilized: {
    isVisible: loan => shouldShowDscrStabilized(loan),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "DSCR (Stabilized)",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DECIMAL_SUBTEXT,
        map: data => defaultField(data, "covenants.dscrStabilized"),
        Cell: tableSideColumnMapper,
        decimals: 2,
        suffix: data => "x"
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.dscrStabilized"
      }
    ]
  },
  dscrBeginTestingDate: {
    isVisible: data => shouldShowField(data, "covenants.dscrBeginTestingDate") || shouldShowField(data, "covenants.dscrFrequency"),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Testing Begins/Frequency",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DATE_SUBTEXT,
        map: data => formatDate(defaultField(data, "covenants.dscrBeginTestingDate"), "MMM D, YYYY"),
        Cell: tableSideColumnMapper,
        decimals: 2,
        subtextMappers: [{ map: data => mapSubTextFrequency(data, "covenants.dscrFrequency") }],
        colSpan: 2
      }
    ]
  },
  dscrCovenants: {
    isVisible: loan => shouldShowDscrCovenants(loan),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "DSCR Covenants",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.dscrCovenants"),
        Cell: tableSideColumnMapper,
        decimals: 2
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.dscrCovenants"
      }
    ]
  },
  dscrType: {
    isVisible: covenants => shouldShowdscrType(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "DSCR Type",
        Cell: tableSideColumnMapper
      },
      {
        // ADD API CONFIG ITEM
        type: SIDE_TABLE_COLUMN_TYPES.STRING_SUBTEXT,
        map: data => defaultField(data, "covenants.dscrType.name"),
        Cell: tableSideColumnMapper,
        colSpan: 2
      }
    ]
  },
  dscrDebtServiceTermTrailingMonths: {
    isVisible: covenants => shouldShowDscrDebtServiceTermTrailingMonths(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Trailing Months",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DECIMAL,
        map: data => defaultField(data, "covenants.dscrDebtServiceTerm"),
        Cell: tableSideColumnMapper,
        colSpan: 2
      }
    ]
  },
  dscrDebtServiceTermProjectedMonths: {
    isVisible: covenants => shouldShowDscrDebtServiceTermProjectedMonths(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Projected Months",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DECIMAL,
        map: data => defaultField(data, "covenants.dscrDebtServiceTerm"),
        Cell: tableSideColumnMapper,
        colSpan: 2
      }
    ]
  },
  dscrAmortTerm: {
    isVisible: covenants => shouldHypotheticalAmortizations(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "DSCR Amortization Term",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DECIMAL,
        map: data => defaultField(data, "covenants.dscrAmortTerm"),
        Cell: tableSideColumnMapper,
        colSpan: 2
      }
    ]
  },
  dscrFixedRate: {
    isVisible: covenants => shouldHypotheticalAmortizations(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "DSCR Fixed Rate",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DECIMAL,
        map: data => defaultField(data, "covenants.dscrFixedRate"),
        Cell: tableSideColumnMapper,
        decimals: 3,
        colSpan: 2
      }
    ]
  },
  dscrTreasurySpread: {
    isVisible: covenants => shouldHypotheticalAmortizations(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "DSCR Treasury Spread",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DECIMAL,
        map: data => defaultField(data, "covenants.dscrTreasurySpread"),
        Cell: tableSideColumnMapper,
        decimals: 3,
        colSpan: 2
      }
    ]
  },
  dscrTreasuryLookback: {
    isVisible: covenants => shouldHypotheticalAmortizations(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "DSCR Treasury Lookback",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DECIMAL,
        map: data => defaultField(data, "covenants.dscrTreasuryLookback"),
        Cell: tableSideColumnMapper,
        colSpan: 2
      }
    ]
  },
  dscrFloatingRateSpread: {
    isVisible: covenants => shouldHypotheticalAmortizations(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "DSCR Floating Rate Spread",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DECIMAL,
        map: data => defaultField(data, "covenants.dscrFloatingRateSpread"),
        Cell: tableSideColumnMapper,
        decimals: 3,
        colSpan: 2
      }
    ]
  },

  debtYieldSubSection: {
    isVisible: covenants => shouldShowDebtYieldSubSection(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        className: "subsection",
        width: DEFAULT_WIDTH,
        label: "Debt Yield",
        Cell: tableSideColumnMapper,
        colSpan: 3
      }
    ]
  },
  debtYieldStabilized: {
    isVisible: covenants => shouldShowDebtYieldStabilized(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Debt Yield (Stabilized)",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT,
        map: data => defaultPercentField(data, "covenants.debtYieldStabilized", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.debtYieldStabilized"
      }
    ]
  },
  debtYieldBeginTestingDate: {
    isVisible: data => shouldShowField(data, "covenants.debtYieldBeginTestingDate") || shouldShowField(data, "covenants.debtYieldFrequency"),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Testing Begins/Frequency",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DATE_SUBTEXT,
        map: data => formatDate(defaultField(data, "covenants.debtYieldBeginTestingDate"), "MMM D, YYYY"),
        Cell: tableSideColumnMapper,
        decimals: 2,
        subtextMappers: [{ map: data => mapSubTextFrequency(data, "covenants.debtYieldFrequency") }],
        colSpan: 2
      }
    ]
  },
  debtYieldCovenants: {
    isVisible: covenants => shouldShowDebtYieldCovenants(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Debt Yield Covenants",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.debtYieldCovenants"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.debtYieldCovenants"
      }
    ]
  },

  ltvSubSection: {
    isVisible: covenants => shouldShowLtvSubSection(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        className: "subsection",
        width: DEFAULT_WIDTH,
        label: "LTV",
        Cell: tableSideColumnMapper,
        colSpan: 3
      }
    ]
  },
  ltvStabilized: {
    isVisible: covenants => shouldShowLtvStabilized(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "LTV (Stabilized)",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT_SUBTEXT,
        map: data => defaultPercentField(data, "covenants.ltvStabilized", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.ltvStabilized"
      }
    ]
  },
  ltvBeginTestingDate: {
    isVisible: data => shouldShowField(data, "covenants.ltvBeginTestingDate") || shouldShowField(data, "covenants.ltvFrequency"),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Testing Begins/Frequency",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DATE_SUBTEXT,
        map: data => formatDate(defaultField(data, "covenants.ltvBeginTestingDate"), "MMM D, YYYY"),
        Cell: tableSideColumnMapper,
        decimals: 2,
        subtextMappers: [{ map: data => mapSubTextFrequency(data, "covenants.ltvFrequency") }],
        colSpan: 2
      }
    ]
  },
  ltvCovenants: {
    isVisible: covenants => shouldShowLtvCovenants(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "LTV Covenants",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.ltvCovenants"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.ltvCovenants"
      }
    ]
  },

  occupancySubSection: {
    isVisible: covenants => shouldShowOccupancySubSection(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        className: "subsection",
        width: DEFAULT_WIDTH,
        label: "Occupancy",
        Cell: tableSideColumnMapper,
        colSpan: 3
      }
    ]
  },
  occupancy: {
    isVisible: covenants => shouldShowOccupancy(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Occupancy",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT_SUBTEXT,
        map: data => defaultPercentField(data, "covenants.occupancy", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.occupancy"
      }
    ]
  },
  occupancyBeginTestingDate: {
    isVisible: data => shouldShowField(data, "covenants.occupancyBeginTestingDate") || shouldShowField(data, "covenants.occupancyFrequency"),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Testing Begins/Frequency",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DATE_SUBTEXT,
        map: data => formatDate(defaultField(data, "covenants.occupancyBeginTestingDate"), "MMM D, YYYY"),
        Cell: tableSideColumnMapper,
        decimals: 2,
        subtextMappers: [{ map: data => mapSubTextFrequency(data, "covenants.occupancyFrequency") }],
        colSpan: 2
      }
    ]
  },
  occupancyCovenants: {
    isVisible: covenants => shouldShowOccupancyCovenants(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Occupancy Covenants",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.occupancyCovenants"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.occupancyCovenants"
      }
    ]
  },

  ltcSubSection: {
    isVisible: covenants => shouldShowLtcSubSection(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        className: "subsection",
        width: DEFAULT_WIDTH,
        label: "LTC",
        Cell: tableSideColumnMapper,
        colSpan: 3
      }
    ]
  },
  ltc: {
    isVisible: covenants => shouldShowLtc(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "LTC",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT_SUBTEXT,
        map: data => defaultPercentField(data, "covenants.ltc", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.ltc"
      }
    ]
  },
  ltcBeginTestingDate: {
    isVisible: data => shouldShowField(data, "covenants.ltcBeginTestingDate") || shouldShowField(data, "covenants.ltcFrequency"),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Testing Begins/Frequency",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DATE_SUBTEXT,
        map: data => formatDate(defaultField(data, "covenants.ltcBeginTestingDate"), "MMM D, YYYY"),
        Cell: tableSideColumnMapper,
        decimals: 2,
        subtextMappers: [{ map: data => mapSubTextFrequency(data, "covenants.ltcFrequency") }],
        colSpan: 2
      }
    ]
  },
  ltcCovenants: {
    isVisible: covenants => shouldShowLtcCovenants(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "LTC Covenants",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.ltcCovenants"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.ltcCovenants"
      }
    ]
  },

  asIsSubSection: {
    isVisible: covenants => shouldShowAsIsSubSection(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        className: "subsection",
        width: DEFAULT_WIDTH,
        label: "AS-IS",
        Cell: tableSideColumnMapper,
        colSpan: 3
      }
    ]
  },

  dscrAsIs: {
    isVisible: covenants => shouldShowDscrAsIs(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "DSCR (As-Is)",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.DECIMAL_SUBTEXT,
        map: data => defaultField(data, "covenants.dscrAsIs"),
        Cell: tableSideColumnMapper,
        decimals: 2,
        suffix: data => "x"
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.dscrAsIs"
      }
    ]
  },

  debtYieldAsIs: {
    isVisible: covenants => shouldShowDebtYieldAsIs(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Debt Yield (As-Is)",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT_SUBTEXT,
        map: data => defaultPercentField(data, "covenants.debtYieldAsIs", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.debtYieldAsIs"
      }
    ]
  },

  ltvAsIs: {
    isVisible: covenants => shouldShowLtvAsIs(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "LTV (As-Is)",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.PERCENT_SUBTEXT,
        map: data => defaultPercentField(data, "covenants.ltvAsIs", 100),
        Cell: tableSideColumnMapper,
        decimals: 3
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.ltvAsIs"
      }
    ]
  },

  financialCovenantsMisc: {
    isVisible: covenants => shouldShowFinancialCovenantsMisc(covenants),
    cells: [
      {
        type: SIDE_TABLE_COLUMN_TYPES.COLUMN,
        width: DEFAULT_WIDTH,
        label: "Misc",
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.TEXT_SUBTEXT,
        map: data => defaultField(data, "covenants.financialCovenantsMisc"),
        Cell: tableSideColumnMapper
      },
      {
        type: SIDE_TABLE_COLUMN_TYPES.REFERENCE,
        Cell: tableSideColumnMapper,
        referencedFieldPath: "loans.covenants.financialCovenantsMisc"
      }
    ]
  }
};
